import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { checkAuthenticationAdmin, checkAuthenticationSuperAdmin } from 'helpers/lib';
import { OrganizationService } from 'services/organization';
import {
  OrganizationLimitation,
  OrganizationParams,
} from 'services/organization/organization.type';

// Query key constants
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (params: OrganizationParams) => [...organizationKeys.lists(), params] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  usageData: (id: string) => [...organizationKeys.detail(id), 'usageData'] as const,
  users: (id: string) => [...organizationKeys.detail(id), 'users'] as const,
  usageCurrent: () => [...organizationKeys.all, 'usageCurrent'] as const,
  usageHistoryCurrent: () => [...organizationKeys.all, 'usageHistoryCurrent'] as const,
};

// Hook to fetch organizations list
export const useOrganizations = (
  params: OrganizationParams = {},
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: organizationKeys.list(params),
    queryFn: () => OrganizationService.getOrganizations(params),
    retry: 1,
    retryDelay: 1000,
    ...options,
  });
};

// Hook to create organization
export const useCreateOrganization = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: OrganizationService.createOrganization,
    onSuccess: (_data) => {
      // Invalidate and refetch organizations list
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
      // Force refetch to ensure data is updated immediately
      client.refetchQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating organization:', error);
      // Error handling will be done at component level
    },
  });
};

// Hook to delete organization
export const useDeleteOrganization = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: OrganizationService.deleteOrganization,
    onSuccess: () => {
      // Invalidate and refetch organizations list
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
      // Force refetch to ensure data is updated immediately
      client.refetchQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Error deleting organization:', error);
    },
  });
};

// Hook to update organization limitations
export const useUpdateOrganizationLimitations = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      limitations,
    }: {
      id: string;
      limitations: Partial<OrganizationLimitation>;
    }) => OrganizationService.updateOrganizationLimitations(id, limitations),
    onSuccess: () => {
      // Invalidate and refetch organizations list
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
      // Force refetch to ensure data is updated immediately
      client.refetchQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating organization limitations:', error);
    },
  });
};

// Hook to fetch organization usage data (includes current usage, history, and details)
export const useOrganizationUsageData = (id: string) => {
  return useQuery({
    queryKey: organizationKeys.usageData(id),
    queryFn: () => OrganizationService.getOrganizationUsageData(id),
    enabled: !!id,
    retry: 1,
    retryDelay: 1000,
  });
};

// /organization/current* is admin-only — the member (user) role gets 403. Only run these for
// admin/superadmin so members (e.g. on the Upload screen) don't spam the endpoint. (#org-usage-403)
const isOrgAdmin = () => !!checkAuthenticationAdmin() || !!checkAuthenticationSuperAdmin();

// Hook to fetch organization usage current
export const useOrganizationUsageCurrent = () => {
  return useQuery({
    queryKey: organizationKeys.usageCurrent(),
    queryFn: () => OrganizationService.getOrganizationUsageCurrent(),
    enabled: isOrgAdmin(),
    retry: false,
  });
};

// Hook to fetch organization usage history current
export const useOrganizationUsageHistoryCurrent = () => {
  return useQuery({
    queryKey: organizationKeys.usageHistoryCurrent(),
    queryFn: () => OrganizationService.getOrganizationUsageHistoryCurrent(),
    enabled: isOrgAdmin(),
    retry: false,
  });
};
// Hook to update organization user
export const useUpdateOrganizationUser = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      userData,
    }: {
      orgId: string;
      userId: string;
      userData: {
        first_name: string;
        last_name: string;
        role: string;
        email: string;
        locale: string;
      };
    }) => {
      console.log('Mutation function called with:', { orgId, userId, userData });
      try {
        const result = await OrganizationService.updateOrganizationUser(orgId, userId, userData);
        console.log('Mutation API call successful:', result);

        // Additional validation - check if result exists and is valid
        if (!result) {
          throw new Error('No response received from API');
        }

        return result;
      } catch (error) {
        console.error('Mutation API call failed:', error);
        throw error; // Re-throw the error to be caught by the component
      }
    },
    onSuccess: (_, { orgId }) => {
      console.log('Mutation onSuccess called, invalidating queries for orgId:', orgId);
      // Invalidate and refetch organization users data
      client.invalidateQueries({ queryKey: organizationKeys.users(orgId) });
      // Also invalidate organizations list to update user count
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Mutation onError called with error:', error);
    },
  });
};

// Hook to delete organization user
export const useDeleteOrganizationUser = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, userId }: { orgId: string; userId: string }) => {
      console.log('Delete mutation function called with:', { orgId, userId });
      try {
        const result = await OrganizationService.deleteOrganizationUser(orgId, userId);
        console.log('Delete mutation API call successful:', result);
        return result;
      } catch (error) {
        console.error('Delete mutation API call failed:', error);
        throw error;
      }
    },
    onSuccess: (_, { orgId }) => {
      console.log('Delete mutation onSuccess called, invalidating queries for orgId:', orgId);
      // Invalidate and refetch organization users data
      client.invalidateQueries({ queryKey: organizationKeys.users(orgId) });
      // Also invalidate organizations list to update user count
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Delete mutation onError called with error:', error);
    },
  });
};

// Hook to create organization user
export const useCreateOrganizationUser = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      userData,
    }: {
      orgId: string;
      userData: {
        email: string;
        role: string;
        first_name: string;
        last_name: string;
        locale: string;
      };
    }) => {
      console.log('Create user mutation function called with:', { orgId, userData });
      try {
        const result = await OrganizationService.createOrganizationUser(orgId, userData);
        console.log('Create user mutation API call successful:', result);
        if (!result) {
          throw new Error('No response received from API');
        }
        return result;
      } catch (error) {
        console.error('Create user mutation API call failed:', error);
        throw error;
      }
    },
    onSuccess: (_, { orgId }) => {
      console.log('Create user mutation onSuccess called, invalidating queries for orgId:', orgId);
      // Invalidate and refetch organization users data
      client.invalidateQueries({ queryKey: organizationKeys.users(orgId) });
      // Also invalidate organizations list to update user count
      client.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
    onError: (error) => {
      console.error('Create user mutation onError called with error:', error);
    },
  });
};

// Hook to fetch organization users
export const useOrganizationUsers = (id: string) => {
  return useQuery({
    queryKey: organizationKeys.users(id),
    queryFn: () => OrganizationService.getOrganizationUsers(id),
    enabled: !!id,
    retry: 1,
    retryDelay: 1000,
  });
};
